import React, { Fragment, ReactElement } from 'react';
import { bindActionCreators, Dispatch } from 'redux';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';

// Components

import Form from '@lib/wrappers/Form';
import UniversalInput from '@lib/wrappers/UniversalInput';
import ValidationErrorMessage from '../notifications/ValidationErrorMessage';

// ActionCreators

import { changeData } from '@actions/ssap/ssapActionCreators';

// Utils

import { normalizeNameOfPerson } from '@utils/normalize/normalizeNameOfPerson';
import { preventerTools } from '@utils/tools/preventerTools';

// Constants

import { DROPDOWN_OPTIONS } from '@constants/ssap/hhmTypes/dropdownOptions';
import { INPUT_TYPES } from '@constants/inputs/inputTypes';
import { VALIDATION_TYPES } from '@constants/inputs/validationTypes';
import { VALIDATION_ERROR_MESSAGES } from '@constants/inputs/validationErrorMessages';

// Types

import {
    ApplicationStore,
    HouseholdMember,
    HouseholdMemberStatusSummary,
    SsapPage,
    SsapReduxActions
} from '@components/ssap/SsapTypes';

/**
 *
 * @param {Object} props
 * @returns {React.Component} - Page 'Parent and Care Taker'
 *
 */
const Page2_26 = (props: SsapPage): ReactElement => {

    const {
        currentHhm,
        data,
        familyStatus,
        form,
        formName,
        hhmIndex : member,
        householdMembers
    } = props;

    const {
        isFormOnTopLevelValid,
        isFormSubmitted
    } = preventerTools.provideObject(form[formName]);

    const {
        name,
        parentCaretaker
    } = currentHhm;

    const onFieldChange = (newData: HouseholdMember): void => {
        const newState = { ...data };
        newState.taxHousehold.householdMember[member] = newData;
        props.actions.changeData(newState);
    };
    const getChildren = (): Array<ReactElement> | null => {
        if(familyStatus.dependentsOfCaretaker.length === 0) return null;

        return (
            familyStatus.dependentsOfCaretaker.map((dependent: HouseholdMemberStatusSummary) => {
                const { personName, personGuid } = dependent;
                return(
                    <li key = { personGuid }>
                        { personName }
                    </li>
                );
            })
        );
    };

    return (
        <div id = 'fhParentCareTaker' className = 'fade-in' >
            <Form { ...{ formName } }>
                <Fragment>
                    <div className = 'subsection' >
                        <ValidationErrorMessage
                            isVisible = { isFormSubmitted && !isFormOnTopLevelValid }
                            messageText = { VALIDATION_ERROR_MESSAGES.ANY_VALUE }
                            errorClasses = { [ 'margin-l-0' ] }
                        />
                        <UniversalInput
                            legend = { {
                                dataInstructions: 'instructional_text.who_is_parent_caretaker',
                                legendText: 'ssap.input.legend.non_internationalized_text',
                                legendTextReplacements: {
                                    text: (
                                        <span className = 'required-true' >
                                            <FormattedMessage
                                                id = 'ssap.family_household.parent_caretaker.who_takes_care'
                                                values = { {
                                                    personName: (
                                                        <strong>
                                                            { normalizeNameOfPerson(name) }
                                                        </strong>
                                                    )
                                                } }
                                            />
                                        </span>
                                    )
                                }
                            }}
                            additionalContent = {
                                <ul className = 'usa-list gi-list--ul' >
                                    { getChildren() }
                                </ul>
                            }
                            label = { {
                                labelClass: [ 'required-false' ],
                                showRequiredIcon: false
                            } }
                            inputData = { {
                                isInputRequired: true,
                                type: INPUT_TYPES.RADIO_BUTTONS,
                                name: 'parentCaretaker',
                                value: parentCaretaker,
                                options: DROPDOWN_OPTIONS.YES_NO,
                                validationType: VALIDATION_TYPES.CHOSEN,
                                currentData: householdMembers[member],
                                fields: []

                            }}
                            inputActions = { {
                                onFieldChange
                            } }
                            errorClasses = { [ 'margin-l-0' ] }
                        />
                    </div>
                </Fragment>
            </Form>
        </div>
    );
};

const mapStateToProps = (state: ApplicationStore): Partial<SsapPage> => ({
    data: state.ssap.data,
    form: state.ssap.form,

    householdMembers: state
        .ssap
        .data
        .taxHousehold
        .householdMember
});

const mapDispatchToProps = (dispatch: Dispatch): { actions: Partial<SsapReduxActions> } => ({
    actions: bindActionCreators({
        changeData
    }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(Page2_26);
